/** Tells someone the AI has stopped, before they discover it mid-exercise.
 *
 * There are now TWO pots, and this has to read both or it lies:
 *
 *   * the ALLOWANCE — one free conversation for an account that has never
 *     paid, or a monthly rupee budget for one that has. It resets (or
 *     doesn't) on its own and cannot be bought;
 *   * bought CREDITS — a balance that never expires.
 *
 * Reading only the credit balance is how this banner used to work, and
 * after the allowance replaced plan-granted credits that made it tell
 * every single new account "your AI allowance is used up" on the morning
 * they signed up, in red, before they had used anything at all. A first
 * screen that opens with a false failure is worse than no banner.
 *
 * So: say nothing while either pot has something in it. When both are
 * empty, say which pot ran out, because the answer differs — a taster
 * never comes back, a monthly budget returns on the 1st.
 *
 * Tone is deliberate too. Nothing is broken here; someone needs to pay.
 * That is amber, not red.
 *
 * Who sees what still matters. Credits belong to the SCHOOL, so a pupil
 * cannot buy and must not be sent to a shop they'd bounce off; they are
 * told who to ask. An admin — including a solo learner, who is their own
 * admin — gets the link.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { myAiUsage, type MyAiUsage } from "../../api/aiUsage";
import * as api from "../../api/store";
import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n";

/** Warn from here on. Early enough to act, late enough not to nag. */
const WARN_AT = 0.8;

export function CreditsBanner() {
  const t = useT();
  const { user } = useAuth();
  const [credits, setCredits] = useState<api.CreditBalance | null>(null);
  const [usage, setUsage] = useState<MyAiUsage | null>(null);

  const tenantId = user?.tenant_id ?? null;
  useEffect(() => {
    if (!tenantId) return;   // super-admins hold neither
    let cancelled = false;
    Promise.all([api.myCredits(), myAiUsage()])
      .then(([c, u]) => {
        if (cancelled) return;
        setCredits(c);
        setUsage(u);
      })
      // A figure we cannot read is not worth an error — the AI features
      // report their own failures.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!credits || !usage) return null;

  const bought = credits.balance;
  // Anything left in EITHER pot means the AI still works. Nothing to say.
  if (usage.remaining > 0 || bought > 0) {
    // ...except the one honest early warning: a subscriber most of the way
    // through this month's budget, with nothing bought to fall back on.
    const share = usage.limit > 0 ? usage.used / usage.limit : 0;
    const nearlyOut =
      usage.kind === "monthly" && bought <= 0 && share >= WARN_AT;
    if (!nearlyOut) return null;
    // A taster is ONE conversation; "80% through it" would be noise.
    return (
      <Banner tone="warning">
        {t("credits.low", { percent: Math.min(99, Math.round(share * 100)) })}
        <Action user={user} to="/store" label={t("credits.topUp")} />
      </Banner>
    );
  }

  // Both empty. WHICH pot ran out changes what to do about it.
  const taster = usage.kind === "taster";
  const canBuy = user?.role === "tenant_admin";
  const message = taster
    ? (canBuy ? t("credits.tasterGone") : t("credits.tasterGoneLearner"))
    : (canBuy ? t("credits.outAdmin") : t("credits.outLearner"));

  return (
    <Banner tone="warning">
      {message}
      <Action
        user={user}
        to={taster ? "/subscribe" : "/store"}
        label={taster ? t("credits.seePlans") : t("credits.topUp")}
      />
    </Banner>
  );
}

function Banner({ tone, children }: { tone: "warning"; children: React.ReactNode }) {
  return (
    <div
      role="status"
      className={`mb-4 rounded-card border px-4 py-3 text-sm ${
        tone === "warning" ? "border-warning/40 bg-warning/10 text-ink" : ""
      }`}
    >
      {children}
    </div>
  );
}

/** The link, shown only to whoever is actually allowed to press it. */
function Action(
  { user, to, label }: { user: { role: string } | null; to: string; label: string },
) {
  if (user?.role !== "tenant_admin") return null;
  return (
    <>
      {" "}
      <Link to={to} className="font-bold underline">
        {label}
      </Link>
    </>
  );
}
